import { useLayoutEffect, useRef, useState } from "react";
import { roundToHalfPixel } from "@/desktop/native-glass-geometry";
import {
  cancelNativeGlassMorph,
  startNativeGlassMorph,
  webAnimationStartTime,
} from "@/desktop/native-glass-motion";
import {
  nativeGlassMorphsEnabled,
  readMaterialAlpha,
  requestNativeGlassRefresh,
} from "@/desktop/native-glass-settings";
import {
  prefersReducedMotion,
  REVEAL_BEZIER,
  REVEAL_DOT_SIZE,
  REVEAL_EASE,
  REVEAL_MS,
  type RevealStage,
} from "@/lib/reveal-motion";

type DotInsets = { top: number; right: number; bottom: number; left: number };

/** Inactive webviews may pause collection rAF. Give native one short receipt
 * window, then restore the timer-backed CSS reveal instead of holding a dot. */
export const STANDALONE_NATIVE_START_WAIT_MS = 80;

const canRevealNatively = () =>
  // Standalone glass has no full-size web coat or supplemental backdrop.
  nativeGlassMorphsEnabled() &&
  document.documentElement.dataset.desktopNativeGlass === "true" &&
  document.documentElement.dataset.desktopNativeGlassMorphStartReceipts ===
    "true" &&
  readMaterialAlpha() < 1 &&
  !prefersReducedMotion();

/** The native plate and the webview clip share the acknowledged native clock.
 * Ownership lasts through idle: the outer reveal timer can finish before a
 * delayed receipt arrives, and must not replace this plate with CSS material. */
export function useStandaloneNativeReveal({
  stage,
  dotInsets,
  enabled,
}: {
  stage: RevealStage;
  dotInsets: DotInsets;
  enabled: boolean;
}) {
  const shellRef = useRef<HTMLFormElement>(null);
  const [nativeReveal, setNativeReveal] = useState(false);
  const [fallbackDurationMs, setFallbackDurationMs] = useState<number | null>(
    null,
  );
  const previousStage = useRef<RevealStage | undefined>(undefined);
  const currentStage = useRef(stage);
  currentStage.current = stage;
  const openStartedAt = useRef<number | null>(null);
  const latestInsets = useRef(dotInsets);
  latestInsets.current = dotInsets;
  const stop = useRef<(() => void) | null>(null);
  const refreshGeometry = useRef<(() => void) | null>(null);

  useLayoutEffect(() => {
    const previous = previousStage.current;
    previousStage.current = stage;
    if (stage === "dot" && previous !== "dot") {
      openStartedAt.current = null;
      setFallbackDurationMs(null);
    } else if (stage === "open" && previous !== "open") {
      openStartedAt.current = Date.now();
    }
    if (!enabled || stage === "exit") {
      stop.current?.();
      stop.current = null;
      setNativeReveal(false);
      return;
    }
    const opening =
      (stage === "dot" && previous !== "dot") ||
      (stage === "open" && previous === undefined);
    if (!opening) return;
    stop.current?.();
    stop.current = null;
    setNativeReveal(false);

    const element = shellRef.current;
    if (!element || !canRevealNatively() || !element.animate) return;
    const rect = element.getBoundingClientRect();
    if (rect.width < 8 || rect.height < 8) return;
    const insets = latestInsets.current;
    const from = {
      x: roundToHalfPixel(rect.x + insets.left),
      y: roundToHalfPixel(rect.y + insets.top),
      width: REVEAL_DOT_SIZE,
      height: REVEAL_DOT_SIZE,
      cornerRadius: REVEAL_DOT_SIZE / 2,
    };
    const fullClip = "inset(0px 0px 0px 0px round 24px)";
    const originalClip = element.style.clipPath;
    let active = true;
    let clipActive = true;
    let animation: Animation | undefined;
    let settleTimer: number | undefined;
    let dotClip = "";
    let observer: ResizeObserver | undefined;
    const updateClip = () => {
      if (!clipActive) return;
      const bounds = element.getBoundingClientRect();
      const x = roundToHalfPixel(bounds.x);
      const y = roundToHalfPixel(bounds.y);
      const width = roundToHalfPixel(bounds.width);
      const height = roundToHalfPixel(bounds.height);
      const next = `inset(${from.y - y}px ${x + width - from.x - from.width}px ${y + height - from.y - from.height}px ${from.x - x}px round 3px)`;
      if (next === dotClip) return;
      const changed = dotClip !== "";
      dotClip = next;
      element.style.clipPath = dotClip;
      // Updating an effect's keyframes keeps its acknowledged startTime.
      const effect = animation?.effect as KeyframeEffect | null;
      if (typeof effect?.setKeyframes === "function") {
        effect.setKeyframes([{ clipPath: dotClip }, { clipPath: fullClip }]);
      }
      if (changed) {
        requestNativeGlassRefresh({
          forceSend: true,
          trackTransition: false,
        });
      }
    };
    const clearClip = () => {
      clipActive = false;
      observer?.disconnect();
      if (refreshGeometry.current === updateClip)
        refreshGeometry.current = null;
      animation?.cancel();
      animation = undefined;
      element.style.clipPath = originalClip;
    };
    const stopReveal = () => {
      active = false;
      window.clearTimeout(startTimer);
      if (settleTimer !== undefined) window.clearTimeout(settleTimer);
      clearClip();
      cancelNativeGlassMorph(element);
    };
    const fallBack = () => {
      if (!active) return;
      stopReveal();
      // The outer hook still reaches idle on its original clock. Fit the
      // newly enabled CSS reveal into the time remaining before that point.
      setFallbackDurationMs(
        currentStage.current === "open" && openStartedAt.current !== null
          ? Math.max(0, REVEAL_MS - (Date.now() - openStartedAt.current))
          : REVEAL_MS,
      );
      setNativeReveal(false);
    };
    stop.current = stopReveal;
    refreshGeometry.current = updateClip;
    // This also holds the dot if the outer hook reaches idle before native
    // has installed its plate. The collector still sees the full final box.
    updateClip();
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(updateClip);
      observer.observe(element);
    }
    setNativeReveal(true);
    const startTimer = window.setTimeout(
      fallBack,
      STANDALONE_NATIVE_START_WAIT_MS,
    );
    startNativeGlassMorph(
      element,
      {
        from,
        durationMs: REVEAL_MS,
        bezier: REVEAL_BEZIER,
        startOnApply: true,
      },
      {
        onStarted: (startedAt) => {
          if (!active) return;
          window.clearTimeout(startTimer);
          updateClip();
          animation = element.animate(
            [{ clipPath: dotClip }, { clipPath: fullClip }],
            { duration: REVEAL_MS, easing: REVEAL_EASE, fill: "both" },
          );
          animation.startTime = webAnimationStartTime(startedAt);
          settleTimer = window.setTimeout(
            clearClip,
            Math.max(0, startedAt + REVEAL_MS - Date.now()),
          );
        },
        onTimeout: fallBack,
      },
    );
  }, [enabled, stage]);

  // Position-only placement changes do not fire ResizeObserver.
  useLayoutEffect(() => {
    refreshGeometry.current?.();
  });

  useLayoutEffect(
    () => () => {
      stop.current?.();
      stop.current = null;
      previousStage.current = undefined;
    },
    [],
  );

  return { shellRef, nativeReveal, fallbackDurationMs };
}
